import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

const WorkoutModeGraph: React.FC = () => {
    const [activeWorkout, setActiveWorkout] = useState<string | null>(null);

    const workoutTypes = [
        { id: "running", name: "دویدن", icon: "🏃", color: "#FF9500" },
        { id: "cycling", name: "دوچرخه", icon: "🚴", color: "#0072CE" },
        { id: "swimming", name: "شنا", icon: "🏊", color: "#00E5FF" },
        { id: "strength", name: "قدرتی", icon: "💪", color: "#FF3B30" },
        { id: "yoga", name: "یوگا", icon: "🧘", color: "#A7FFEB" },
    ];

    const recentWorkout = {
        type: "running",
        date: "امروز",
        duration: "۴۵ دقیقه",
        distance: "۶.۵ کیلومتر",
        avgHR: 145,
        maxHR: 172,
        calories: 485,
        pace: "۶:۵۵ دقیقه/کیلومتر",
        zones: [
            { name: "گرم کردن", duration: 5, hr: 110, color: "#32D74B" },
            { name: "چربی‌سوزی", duration: 15, hr: 135, color: "#66D4CF" },
            { name: "قلبی-عروقی", duration: 20, hr: 155, color: "#FFD60A" },
            { name: "خنک کردن", duration: 5, hr: 100, color: "#32D74B" },
        ],
    };

    const hrZones = [
        { name: "استراحت", min: 60, max: 100, color: "#32D74B" },
        { name: "چربی‌سوزی", min: 100, max: 130, color: "#66D4CF" },
        { name: "قلبی-عروقی", min: 130, max: 160, color: "#FFD60A" },
        { name: "آستانه بی‌هوازی", min: 160, max: 180, color: "#FF9500" },
        { name: "حداکثر", min: 180, max: 200, color: "#FF3B30" },
    ];

    const weeklyStats = [
        { day: "ش", workouts: 1, duration: 30, calories: 250 },
        { day: "ی", workouts: 0, duration: 0, calories: 0 },
        { day: "د", workouts: 1, duration: 45, calories: 380 },
        { day: "س", workouts: 2, duration: 75, calories: 620 },
        { day: "چ", workouts: 1, duration: 60, calories: 450 },
        { day: "پ", workouts: 0, duration: 0, calories: 0 },
        { day: "ج", workouts: 1, duration: 45, calories: 485 },
    ];

    return (
        <div className="space-y-3" dir="rtl">
            <div>
                <h4 className="font-medium text-[#002B5C] mb-3">آخرین تمرین شما</h4>

                {/* Workout Summary Card */}
                <div className="bg-gradient-to-br from-[#FF9500]/20 to-[#FF9500]/5 p-4 rounded-[20px] mb-4 shadow-[0_4px_16px_rgba(0,0,0,0.04)] border border-[#FF9500]/30">
                    <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                            <div className="text-4xl">🏃</div>
                            <div>
                                <div className="text-lg font-bold text-[#FF9500]">{recentWorkout.type === "running" ? "دویدن" : recentWorkout.type}</div>
                                <div className="text-xs text-[#9EA3A9]">{recentWorkout.date}</div>
                            </div>
                        </div>
                        <div className="text-right">
                            <div className="text-2xl font-bold text-[#FF9500]">{recentWorkout.duration}</div>
                            <div className="text-xs text-[#9EA3A9]">مدت زمان</div>
                        </div>
                    </div>

                    <div className="grid grid-cols-3 gap-3">
                        <div className="bg-white/80 p-2 rounded-[12px] text-center">
                            <div className="text-lg font-medium text-[#FF9500]">{recentWorkout.distance}</div>
                            <div className="text-xs text-[#9EA3A9]">مسافت</div>
                        </div>
                        <div className="bg-white/80 p-2 rounded-[12px] text-center">
                            <div className="text-lg font-medium text-[#FF9500]">{recentWorkout.avgHR}</div>
                            <div className="text-xs text-[#9EA3A9]">میانگین ضربان</div>
                        </div>
                        <div className="bg-white/80 p-2 rounded-[12px] text-center">
                            <div className="text-lg font-medium text-[#FF9500]">{recentWorkout.calories}</div>
                            <div className="text-xs text-[#9EA3A9]">کالری</div>
                        </div>
                    </div>
                </div>

                {/* Heart Rate Zones */}
                <h4 className="font-medium text-[#002B5C] mb-2">زمان در منطقه‌های ضربان قلب</h4>
                <div className="space-y-2 mb-4">
                    {recentWorkout.zones.map((zone, i) => {
                        const percentage = (zone.duration / 45) * 100;
                        return (
                            <div key={i}>
                                <div className="flex items-center justify-between mb-1">
                                    <span className="text-xs text-[#4A5568]">{zone.name}</span>
                                    <span className="text-xs font-medium" style={{ color: zone.color }}>
                                        {zone.duration} دقیقه ({zone.hr} BPM)
                                    </span>
                                </div>
                                <div className="w-full bg-[#ECEFF1] rounded-full h-2">
                                    <motion.div
                                        className="h-2 rounded-full"
                                        style={{ backgroundColor: zone.color }}
                                        initial={{ width: 0 }}
                                        animate={{ width: `${percentage}%` }}
                                        transition={{ duration: 0.8, delay: i * 0.1 }}
                                    />
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* HR Zone Chart */}
                <h4 className="font-medium text-[#002B5C] mb-2">نمودار ضربان قلب</h4>
                <div className="h-32 bg-[#ECEFF1] rounded-[16px] p-3 mb-4 shadow-[0_2px_8px_rgba(0,0,0,0.02)] relative">
                    <svg className="w-full h-full" viewBox="0 0 360 100">
                        {/* HR Zones Background */}
                        {hrZones.map((zone, i) => (
                            <rect key={i} x="0" y={100 - ((zone.max - 60) / 140) * 100} width="360" height={((zone.max - zone.min) / 140) * 100} fill={zone.color} opacity="0.1" />
                        ))}

                        {/* HR Curve */}
                        <motion.path
                            d="M 0 70 Q 45 65 90 40 T 180 35 Q 225 30 270 45 T 360 70"
                            fill="none"
                            stroke="#FF9500"
                            strokeWidth="3"
                            initial={{ pathLength: 0 }}
                            animate={{ pathLength: 1 }}
                            transition={{ duration: 2 }}
                        />

                        {/* Max HR Point */}
                        <motion.circle cx="180" cy="30" r="5" fill="#FF3B30" stroke="white" strokeWidth="2" initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 1 }} />
                    </svg>

                    <div className="absolute bottom-2 left-0 right-0 flex justify-between text-[10px] text-[#9EA3A9] px-3">
                        <span>شروع</span>
                        <span>۱۵ دقیقه</span>
                        <span>۳۰ دقیقه</span>
                        <span>پایان</span>
                    </div>
                </div>

                {/* Workout Types Selector */}
                <h4 className="font-medium text-[#002B5C] mb-2">انتخاب نوع تمرین</h4>
                <div className="grid grid-cols-5 gap-2 mb-4">
                    {workoutTypes.map((workout) => (
                        <motion.button
                            key={workout.id}
                            className={`p-3 rounded-[16px] text-center transition-all ${
                                activeWorkout === workout.id ? "bg-gradient-to-br from-[#0072CE]/20 to-[#00E5FF]/10 border-2 border-[#0072CE]" : "bg-[#ECEFF1] border-2 border-transparent"
                            }`}
                            onClick={() => setActiveWorkout(workout.id)}
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                        >
                            <div className="text-2xl mb-1">{workout.icon}</div>
                            <div className="text-xs text-[#4A5568]">{workout.name}</div>
                        </motion.button>
                    ))}
                </div>

                {/* Weekly Activity */}
                <h4 className="font-medium text-[#002B5C] mb-2">فعالیت هفتگی</h4>
                <div className="bg-[#ECEFF1] p-4 rounded-[16px] shadow-[0_2px_8px_rgba(0,0,0,0.02)]">
                    <div className="flex items-end justify-between h-24 mb-3">
                        {weeklyStats.map((stat, i) => {
                            const height = (stat.duration / 75) * 100;
                            return (
                                <div key={i} className="flex flex-col items-center flex-1">
                                    <motion.div
                                        className="w-8 rounded-t-[4px]"
                                        style={{
                                            backgroundColor: stat.workouts > 0 ? "#FF9500" : "#ECEFF1",
                                        }}
                                        initial={{ height: 0 }}
                                        animate={{ height: `${height}%` }}
                                        transition={{ duration: 0.5, delay: i * 0.1 }}
                                    />
                                    <span className="text-xs text-[#9EA3A9] mt-2">{stat.day}</span>
                                </div>
                            );
                        })}
                    </div>

                    <div className="grid grid-cols-3 gap-3 pt-3 border-t border-[#9EA3A9]/20">
                        <div className="text-center">
                            <div className="text-lg font-bold text-[#FF9500]">۶</div>
                            <div className="text-xs text-[#9EA3A9]">تمرین</div>
                        </div>
                        <div className="text-center">
                            <div className="text-lg font-bold text-[#FF9500]">۵ ساعت</div>
                            <div className="text-xs text-[#9EA3A9]">مدت</div>
                        </div>
                        <div className="text-center">
                            <div className="text-lg font-bold text-[#FF9500]">۲۱۸۵</div>
                            <div className="text-xs text-[#9EA3A9]">کالری</div>
                        </div>
                    </div>
                </div>

                {/* Performance Insights */}
                <div className="mt-4 p-3 bg-gradient-to-r from-[#FF9500]/10 to-[#FF3B30]/10 rounded-[16px] border border-[#FF9500]/30">
                    <h5 className="text-sm font-medium text-[#FF9500] mb-2 flex items-center gap-2">
                        <span>🎯</span>
                        تحلیل عملکرد
                    </h5>
                    <ul className="text-xs text-[#4A5568] space-y-1">
                        <li className="flex items-start gap-2">
                            <span className="text-[#32D74B] mt-0.5">✓</span>
                            <span>سرعت متوسط شما نسبت به هفته گذشته ۱۲٪ بهبود یافته است.</span>
                        </li>
                        <li className="flex items-start gap-2">
                            <span className="text-[#0072CE] mt-0.5">•</span>
                            <span>زمان بازیابی بعد از تمرین: ۱۸ ساعت (عالی)</span>
                        </li>
                        <li className="flex items-start gap-2">
                            <span className="text-[#FF9500] mt-0.5">⚡</span>
                            <span>توصیه: برای بهبود استقامت، یک تمرین طولانی‌تر در هفته اضافه کنید.</span>
                        </li>
                    </ul>
                </div>

                {/* Live Workout Guidance */}
                <div className="mt-3 p-3 bg-[#ECEFF1] rounded-[16px] shadow-[0_2px_8px_rgba(0,0,0,0.02)]">
                    <h5 className="text-sm font-medium text-[#002B5C] mb-2 flex items-center gap-2">
                        <span>📱</span>
                        راهنمای تمرین زنده
                    </h5>
                    <div className="text-xs text-[#4A5568] space-y-2">
                        <div className="flex items-center justify-between p-2 bg-white rounded-[12px]">
                            <span>نوتیفیکیشن صوتی</span>
                            <div className="w-10 h-5 bg-[#32D74B] rounded-full relative">
                                <div className="absolute right-0.5 top-0.5 w-4 h-4 bg-white rounded-full"></div>
                            </div>
                        </div>
                        <div className="flex items-center justify-between p-2 bg-white rounded-[12px]">
                            <span>هشدار منطقه قلبی</span>
                            <div className="w-10 h-5 bg-[#32D74B] rounded-full relative">
                                <div className="absolute right-0.5 top-0.5 w-4 h-4 bg-white rounded-full"></div>
                            </div>
                        </div>
                        <div className="flex items-center justify-between p-2 bg-white rounded-[12px]">
                            <span>پیشنهاد استراحت</span>
                            <div className="w-10 h-5 bg-[#ECEFF1] rounded-full relative">
                                <div className="absolute left-0.5 top-0.5 w-4 h-4 bg-white rounded-full"></div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Goals & Achievements */}
                <div className="mt-3 p-3 bg-gradient-to-r from-[#FFD60A]/20 to-[#FF9500]/10 rounded-[16px] border border-[#FFD60A]/30">
                    <h5 className="text-sm font-medium text-[#FF9500] mb-2 flex items-center gap-2">
                        <span>🏆</span>
                        اهداف و دستاوردها
                    </h5>
                    <div className="space-y-2">
                        <div className="flex items-center justify-between">
                            <span className="text-xs text-[#4A5568]">هدف هفتگی: ۵ ساعت</span>
                            <span className="text-xs font-medium text-[#32D74B]">✓ تکمیل شد</span>
                        </div>
                        <div className="w-full bg-white h-2 rounded-full">
                            <motion.div className="h-2 rounded-full bg-gradient-to-r from-[#32D74B] to-[#66D4CF]" initial={{ width: 0 }} animate={{ width: "100%" }} transition={{ duration: 1.5 }} />
                        </div>

                        <div className="grid grid-cols-3 gap-2 mt-3">
                            <div className="bg-white/80 p-2 rounded-[12px] text-center">
                                <div className="text-xl mb-1">🔥</div>
                                <div className="text-xs text-[#9EA3A9]">۷ روز متوالی</div>
                            </div>
                            <div className="bg-white/80 p-2 rounded-[12px] text-center">
                                <div className="text-xl mb-1">⭐</div>
                                <div className="text-xs text-[#9EA3A9]">سطح ۵</div>
                            </div>
                            <div className="bg-white/80 p-2 rounded-[12px] text-center">
                                <div className="text-xl mb-1">🎖️</div>
                                <div className="text-xs text-[#9EA3A9]">۱۲ مدال</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default WorkoutModeGraph;
